// Transfer Orders board — two sections, one per direction. Bengaluru → Hyderabad has a single
// receiving warehouse, so one card. Hyderabad → Bengaluru aggregates its three Bengaluru warehouses
// into ONE summed card; clicking it opens a drill-down that splits the docks out as tabs. Every card
// figure drills down through the page's onDrill handler; the section builds the request (direction +
// docks + per-dock badge counts for the clicked figure).
import { Tooltip } from "antd";
import React from "react";
import styles from "../index.module.scss";
import { TransferDockKey, TransferOrdersSummary, TransferStatusKey } from "../control_tower.api";
import {
  TRANSFER_DIRECTIONS,
  TRANSFER_DIRECTION_LABEL,
  TRANSFER_DOCK_LABEL,
  TRANSFER_EMPTY_COUNTS,
  TRANSFER_SECTION_ICON,
  sumTransferCounts,
} from "../transfer_orders_meta";
import TransferOrderCard from "./transfer_order_card";
import { TransferDrilldownRequest } from "./transfer_orders_drawer";

interface Props {
  loading: boolean;
  data?: TransferOrdersSummary | null;
  onDrill: (request: TransferDrilldownRequest) => void;
}

const TransferOrdersSection: React.FC<Props> = ({ loading, data, onDrill }) => (
  // The two directions side by side — Bengaluru → Hyderabad on the left, Hyderabad → Bengaluru on
  // its right — wrapping to stacked only when the row can't fit.
  <div style={{ display: "flex", gap: 40, flexWrap: "wrap", alignItems: "flex-start" }}>
    {TRANSFER_DIRECTIONS.map((dir) => {
      const cell = (dock: TransferDockKey) => data?.[dir.key]?.[dock];
      // Each rendered card + the docks it spans (one dock, or all of them when aggregated).
      const cards = dir.aggregate
        ? [
            {
              key: "all",
              label: dir.aggregateLabel ?? "All warehouses",
              docks: dir.docks,
              counts: sumTransferCounts(dir.docks.map(cell)),
            },
          ]
        : dir.docks.map((dock) => ({
            key: dock,
            label: TRANSFER_DOCK_LABEL[dock],
            docks: [dock],
            counts: cell(dock) ?? TRANSFER_EMPTY_COUNTS,
          }));

      // Build the drill-down request for a clicked figure (a lifecycle status, the error overlay, or
      // the total). The drawer derives its warehouse tabs from the fetched legs, so only the
      // direction + which figure was clicked is needed here.
      const drill = (opts: { status?: TransferStatusKey; hasError?: boolean }) =>
        onDrill({ direction: dir.key, status: opts.status, hasError: opts.hasError });

      return (
        <div key={dir.key} className={styles.groupRow} style={{ marginBottom: 0 }}>
          <Tooltip title={dir.help}>
            <div className={styles.sectionTitle}>
              <span className={styles.groupIcon}>{TRANSFER_SECTION_ICON}</span>
              {TRANSFER_DIRECTION_LABEL[dir.key]}
            </div>
          </Tooltip>
          <div className={styles.laneGrid}>
            {cards.map((card) => (
              <TransferOrderCard
                key={card.key}
                label={card.label}
                counts={card.counts}
                loading={loading}
                onDrill={drill}
              />
            ))}
          </div>
        </div>
      );
    })}
  </div>
);

export default TransferOrdersSection;
